// Reducer for managing dialog and form state in workflow history
export const initialState = {
	dialogs: {
		deleteConfirm: false,
		cloneConfirm: false,
		// Reassign: the open task and its candidates ({ taskId, taskName, currentAssignee, candidates }).
		reassignOpen: false,
		reassign: null,
		// Download popup: the process's ready documents to choose from (only when there are several).
		documentsOpen: false,
		documents: [],
		// Regenerate: the process's documents offered in the picker (ProcessDocumentResponse[]).
		regenerateOpen: false,
		regenerateDocuments: [],
		// Regenerate, second step: the chosen document and its template's versions to pick from.
		regenerateVersionOpen: false,
		regenerateDocument: null,
		regenerateVersions: [],
		selectedRecord: null,
	},
	table: {
		currentPage: 1,
		recordsPerPage: 10,
		// Empty = the backend default (latest activity first).
		sortField: "",
		sortDirection: "",
		// True when the sorted column is sorted by its translated label (column config sortByLabel).
		sortByLabel: false,
		searchInput: "",
		startDateInput: "",
		endDateInput: "",
		searchParams: {
			searchTerm: "",
			startDate: "",
			endDate: "",
		},
	},
	loading: {
		isLoadingBackdrop: false,
		isBackgroundRefreshing: false,
	},
};

// Every action that changes which rows match (search, sort, page size) also goes back to page 1,
// here in one place, so no handler has to remember to do it.
export function workflowHistoryReducer(state, action) {
	switch (action.type) {
		case "SET_PAGE":
			return {
				...state,
				table: {
					...state.table,
					currentPage: action.payload,
				},
			};
		case "SET_RECORDS_PER_PAGE":
			return {
				...state,
				table: {
					...state.table,
					recordsPerPage: action.payload,
					currentPage: 1,
				},
			};
		case "CLEAR_FILTERS":
			return {
				...state,
				table: {
					...state.table,
					searchInput: initialState.table.searchInput,
					startDateInput: initialState.table.startDateInput,
					endDateInput: initialState.table.endDateInput,
					searchParams: initialState.table.searchParams,
					sortField: initialState.table.sortField,
					sortDirection: initialState.table.sortDirection,
					sortByLabel: initialState.table.sortByLabel,
					currentPage: 1,
				},
			};
		case "OPEN_DELETE_DIALOG":
			return {
				...state,
				dialogs: {
					...state.dialogs,
					deleteConfirm: true,
					selectedRecord: action.payload,
				},
			};
		case "CLOSE_DELETE_DIALOG":
			return {
				...state,
				dialogs: {
					...state.dialogs,
					deleteConfirm: false,
					selectedRecord: null,
				},
			};
		case "OPEN_CLONE_DIALOG":
			return {
				...state,
				dialogs: {
					...state.dialogs,
					cloneConfirm: true,
					selectedRecord: action.payload,
				},
			};
		case "CLOSE_CLONE_DIALOG":
			return {
				...state,
				dialogs: {
					...state.dialogs,
					cloneConfirm: false,
					selectedRecord: null,
				},
			};
		case "OPEN_REASSIGN_DIALOG":
			return {
				...state,
				dialogs: {
					...state.dialogs,
					reassignOpen: true,
					reassign: action.payload,
				},
			};
		case "CLOSE_REASSIGN_DIALOG":
			return {
				...state,
				dialogs: {
					...state.dialogs,
					reassignOpen: false,
					reassign: null,
				},
			};
		case "OPEN_REGENERATE_DIALOG":
			return {
				...state,
				dialogs: {
					...state.dialogs,
					regenerateOpen: true,
					regenerateDocuments: action.payload,
				},
			};
		case "CLOSE_REGENERATE_DIALOG":
			return {
				...state,
				dialogs: {
					...state.dialogs,
					regenerateOpen: false,
					regenerateDocuments: [],
				},
			};
		// payload: { document, versions } - the document to regenerate and its template's versions.
		case "OPEN_REGENERATE_VERSION_DIALOG":
			return {
				...state,
				dialogs: {
					...state.dialogs,
					regenerateVersionOpen: true,
					regenerateDocument: action.payload.document,
					regenerateVersions: action.payload.versions,
				},
			};
		case "CLOSE_REGENERATE_VERSION_DIALOG":
			return {
				...state,
				dialogs: {
					...state.dialogs,
					regenerateVersionOpen: false,
					regenerateDocument: null,
					regenerateVersions: [],
				},
			};
		case "OPEN_DOCUMENTS_DIALOG":
			return {
				...state,
				dialogs: {
					...state.dialogs,
					documentsOpen: true,
					documents: action.payload,
				},
			};
		case "CLOSE_DOCUMENTS_DIALOG":
			return {
				...state,
				dialogs: {
					...state.dialogs,
					documentsOpen: false,
					documents: [],
				},
			};
		case "SET_SORT":
			return {
				...state,
				table: {
					...state.table,
					sortField: action.payload.field,
					sortDirection: action.payload.direction,
					sortByLabel: action.payload.byLabel === true,
					currentPage: 1,
				},
			};
		case "SET_SEARCH_INPUT":
			return {
				...state,
				table: {
					...state.table,
					searchInput: action.payload,
				},
			};
		case "SET_START_DATE_INPUT":
			return {
				...state,
				table: {
					...state.table,
					startDateInput: action.payload,
				},
			};
		case "SET_END_DATE_INPUT":
			return {
				...state,
				table: {
					...state.table,
					endDateInput: action.payload,
				},
			};
		case "SET_SEARCH_PARAMS":
			return {
				...state,
				table: {
					...state.table,
					searchParams: action.payload,
					currentPage: 1,
				},
			};
		case "SET_LOADING":
			return {
				...state,
				loading: {
					...state.loading,
					[action.payload.key]: action.payload.value,
				},
			};
		default:
			return state;
	}
}
